// src/pages/offlineSales/ReceivablesPage.jsx
//
// Outstanding customer balances (aging summary) for Offline Sales. Layout mirrors
// src/pages/returnBatch/ReturnBatchListPage.jsx (PageHeader + FilterPanel + DataTable).
//
// The backend's getAgingSummary (rettzy-backend/src/modules/offlineSales/services/
// receivables.service.js) returns the WHOLE business's open receivables in one call —
// no server-side search/pagination/sort exists for this endpoint, so filtering and
// paging happen client-side here. It returns: { buckets, totalOutstanding, customers:
// [{ customerId, customerName, customerPhone, totalOutstanding, buckets }] } — there is
// no "credit limit" field on this response (that lives on the Customer record, not this
// read model), so it is intentionally NOT shown as a column here rather than invented.
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wallet, Users, History, FileText, Clock, User, Phone, HandCoins, Banknote, PiggyBank } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { getAgingSummary } from '../../services/api/offlineSales/receivables';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import PurchaseHistoryModal from './components/PurchaseHistoryModal';
import tb from '../procurement/components/ProcurementTable.module.css';
import styles from './ReceivablesPage.module.css';

const BUCKET_KEYS = ['0-30', '31-60', '61-90', '90+'];
const BUCKET_LABEL_KEYS = {
  '0-30': 'aging0to30',
  '31-60': 'aging31to60',
  '61-90': 'aging61to90',
  '90+': 'aging90plus',
};

const ReceivablesPage = () => {
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { error: showError } = useAlert();
  const { hasPermission } = usePermissions();
  const canReceivePayment = hasPermission('edit_offline_sales_order');

  const [summary, setSummary] = useState({ buckets: {}, totalOutstanding: 0, pdcInHand: 0, unallocatedCredit: 0, customers: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [search, setSearch] = useState('');
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    pageSizeOptions: [20, 50, 100],
  });

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSummary = async () => {
    try {
      setIsLoading(true);
      const result = await getAgingSummary();
      setSummary({
        buckets: result.buckets || {},
        totalOutstanding: result.totalOutstanding || 0,
        pdcInHand: result.pdcInHand || 0,
        unallocatedCredit: result.unallocatedCredit || 0,
        customers: result.customers || [],
      });
    } catch (err) {
      console.error('Failed to fetch receivables:', err);
      showError(t('offlineSales.failedToLoadReceivables'));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCustomers = useMemo(() => {
    if (!search.trim()) return summary.customers;
    const q = search.trim().toLowerCase();
    return summary.customers.filter(c =>
      (c.customerName || '').toLowerCase().includes(q) ||
      (c.customerPhone || '').includes(q)
    );
  }, [summary.customers, search]);

  const pagedCustomers = useMemo(() => {
    const start = (pagination.currentPage - 1) * pagination.pageSize;
    return filteredCustomers.slice(start, start + pagination.pageSize);
  }, [filteredCustomers, pagination.currentPage, pagination.pageSize]);

  const handleSearchChange = (value) => {
    setSearch(value);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  const columns = [
    {
      key: 'customerName',
      header: t('offlineSales.customer'),
      render: (row) => (
        <div className={tb.partyInfo}>
          <div className={tb.partyName}><User size={14} />{row.customerName}</div>
          {row.customerPhone && <div className={tb.partyMeta}><Phone size={12} />{row.customerPhone}</div>}
        </div>
      ),
    },
    {
      key: 'totalOutstanding',
      header: t('offlineSales.totalOutstanding'),
      align: 'right',
      sortable: false,
      render: (row) => (
        <span className={styles.outstandingAmount}>{formatCurrency(row.totalOutstanding)}</span>
      ),
    },
    {
      key: 'buckets',
      header: t('offlineSales.aging0to30') + ' / ' + t('offlineSales.aging31to60') + ' / ' +
        t('offlineSales.aging61to90') + ' / ' + t('offlineSales.aging90plus'),
      render: (row) => (
        <div className={tb.agingPills}>
          {BUCKET_KEYS.map(key => {
            const amount = row.buckets?.[key] || 0;
            if (amount <= 0) return null;
            const aged = key === '61-90' || key === '90+';
            return (
              <span key={key} className={`${tb.agingPill} ${aged ? tb.agingPillAged : ''}`}>
                {t(`offlineSales.${BUCKET_LABEL_KEYS[key]}`)}: {formatCurrency(amount)}
              </span>
            );
          })}
        </div>
      ),
    },
  ];

  const renderActions = (row) => (
    <div className={tb.actionsWrapper}>
      {canReceivePayment && (
        <button
          className={`${tb.actionButton} ${tb.actionSuccess}`}
          onClick={() => navigate(`/offline-sales/customer-payments/new?customerId=${row.customerId}`)}
          title={t('offlineSales.cpReceivePayment')}
        >
          <HandCoins size={14} />
        </button>
      )}
      <button
        className={`${tb.actionButton} ${tb.viewButton}`}
        onClick={() => setHistoryCustomer({ id: row.customerId, name: row.customerName })}
        title={t('offlineSales.viewHistory')}
      >
        <History size={14} />
      </button>
      <button
        className={`${tb.actionButton} ${tb.viewButton}`}
        onClick={() => navigate(`/offline-sales/customers/${row.customerId}/statement`)}
        title={t('offlineSales.customerStatement') || 'Customer Statement'}
      >
        <FileText size={14} />
      </button>
    </div>
  );

  return (
    <div className={styles.receivablesPage}>
      <PageHeader
        title={t('offlineSales.receivables')}
        description={t('offlineSales.receivablesDescription')}
        icon={Wallet}
      />

      <div className={styles.summaryCards}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryCardLabel}><Wallet size={14} />{t('offlineSales.totalOutstanding')}</div>
          <div className={`${styles.summaryCardValue} ${styles.summaryCardValueTotal}`}>
            {formatCurrency(summary.totalOutstanding)}
          </div>
        </div>
        {BUCKET_KEYS.map(key => (
          <div className={styles.summaryCard} key={key}>
            <div className={styles.summaryCardLabel}><Clock size={14} />{t(`offlineSales.${BUCKET_LABEL_KEYS[key]}`)}</div>
            <div className={styles.summaryCardValue}>{formatCurrency(summary.buckets?.[key] || 0)}</div>
          </div>
        ))}
        <button
          type="button"
          className={`${styles.summaryCard} ${styles.summaryCardLink}`}
          onClick={() => navigate('/offline-sales/cheques')}
        >
          <div className={styles.summaryCardLabel}><Banknote size={14} />{t('offlineSales.chqPdcInHand')}</div>
          <div className={styles.summaryCardValue}>{formatCurrency(summary.pdcInHand)}</div>
        </button>
        <div className={styles.summaryCard}>
          <div className={styles.summaryCardLabel}><PiggyBank size={14} />{t('offlineSales.cpUnallocatedCredit')}</div>
          <div className={styles.summaryCardValue}>{formatCurrency(summary.unallocatedCredit)}</div>
        </div>
      </div>

      <FilterPanel
        filters={[]}
        values={{}}
        onChange={() => {}}
        onClear={() => handleSearchChange('')}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        searchConfig={{
          show: true,
          value: search,
          onChange: handleSearchChange,
          placeholder: t('offlineSales.searchByCustomerName'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={pagedCustomers}
        columns={columns}
        actions={{ render: renderActions, width: '140px' }}
        loading={isLoading}
        pagination={{ ...pagination, total: filteredCustomers.length }}
        onPaginationChange={setPagination}
        emptyMessage={t('offlineSales.noReceivablesFound')}
        emptyDescription={t('offlineSales.noReceivablesFoundDescription')}
        emptyIcon={<Users size={64} />}
      />

      {historyCustomer && (
        <PurchaseHistoryModal
          customer={historyCustomer}
          onClose={() => setHistoryCustomer(null)}
        />
      )}
    </div>
  );
};

export default ReceivablesPage;
